import { makeApi, Zodios } from "@zodios/core";
import { z } from "zod";
const CreateOrgIdentityOrg_Body = z
    .object({ name: z.string(), slug: z.string().optional() })
    .passthrough();
const PatchOrgIdentityOrg_Body = z
    .object({
    name: z.string(),
    slug: z.string(),
    status: z.enum(["active", "suspended", "disabled"]),
    domain: z.string(),
    settings: z
        .object({
        allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
        requireMfa: z.boolean().default(false),
        sessionTimeoutMinutes: z.number().int(),
        maxSessionsPerUser: z.number().int(),
        timezone: z.string(),
        locale: z.string(),
        defaultCurrency: z.string(),
        featureFlags: z.record(z.boolean()),
        enabledSocialChannelSlugs: z.array(z.string()),
    })
        .partial()
        .passthrough(),
    metadata: z.object({}).partial().passthrough(),
})
    .partial()
    .passthrough();
const CreateOrgIdentitySession_Body = z
    .object({ email: z.string().email(), password: z.string() })
    .passthrough();
const RefreshOrgIdentitySession_Body = z
    .object({ refreshToken: z.string() })
    .passthrough();
const PatchOrgIdentityProfile_Body = z
    .object({
    displayName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    profile: z
        .object({ timezone: z.string(), locale: z.string() })
        .partial()
        .passthrough(),
})
    .partial()
    .passthrough();
const CreateOrgIdentityUser_Body = z
    .object({
    email: z.string().email(),
    displayName: z.string().optional(),
    roles: z
        .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
        .optional(),
})
    .passthrough();
const PatchOrgIdentityUser_Body = z
    .object({
    displayName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: z.union([z.string(), z.null()]),
    phone: z.union([z.string(), z.null()]),
    roles: z.array(z.enum(["platform_admin", "org_admin", "user", "viewer"])),
    role: z.string(),
    status: z.enum([
        "active",
        "suspended",
        "invited",
        "deactivated",
        "pending_verification",
        "disabled",
    ]),
    profile: z
        .object({
        department: z.string(),
        title: z.string(),
        timezone: z.string(),
        locale: z.string(),
    })
        .partial()
        .passthrough(),
    metadata: z.object({}).partial().passthrough(),
    tags: z.array(z.string()),
})
    .partial()
    .passthrough();
const CompleteOrgIdentityUserOnboardingStep_Body = z
    .object({ stepId: z.string() })
    .passthrough();
const CreateOrgIdentityRole_Body = z
    .object({
    name: z.string(),
    description: z.string().optional(),
    permissions: z.array(z.string()).optional(),
})
    .passthrough();
const UpdateOrgIdentityRole_Body = z
    .object({
    name: z.string(),
    description: z.string(),
    permissions: z.array(z.string()),
})
    .partial()
    .passthrough();
const CreateOrgIdentityApiKey_Body = z
    .object({
    name: z.string(),
    scopes: z
        .array(z.enum([
        "mcp:invoke",
        "mcp:admin",
        "content:read",
        "content:write",
        "publishing:read",
        "publishing:write",
        "publishing:dispatch",
        "social:read",
        "social:write",
        "social:reply",
        "connectivity:read",
        "connectivity:write",
        "focus:read",
        "focus:write",
        "analytics:read",
        "knowledge:read",
        "knowledge:write",
    ]))
        .optional(),
    expiresAt: z.union([z.string(), z.null()]).optional(),
    environment: z.enum(["live", "test"]).optional().default("live"),
})
    .passthrough();
const CreateOrgIdentitySSOProvider_Body = z
    .object({
    providerType: z.enum(["saml", "oidc", "oauth2"]),
    providerId: z.string(),
    name: z.string().optional(),
    description: z.string().optional(),
    enabled: z.boolean().optional().default(true),
    samlMetadataUrl: z.string().url().optional(),
    samlMetadataXml: z.string().optional(),
    samlEntityId: z.string().optional(),
    samlAcsUrl: z.string().url().optional(),
    samlSigningCertificate: z.string().optional(),
    oidcDiscoveryUrl: z.string().url().optional(),
    oidcClientId: z.string().optional(),
    oidcClientSecret: z.string().optional(),
    oidcScopes: z.array(z.string()).optional(),
    attributeMapping: z.object({}).partial().passthrough().optional(),
    jitProvisioning: z.boolean().optional().default(false),
    enforceSSO: z.boolean().optional().default(false),
    defaultRoles: z.array(z.string()).optional(),
    metadata: z.object({}).partial().passthrough().optional(),
})
    .passthrough();
const UpdateOrgIdentitySSOProvider_Body = z
    .object({
    name: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    samlMetadataUrl: z.string().url(),
    samlMetadataXml: z.string(),
    samlEntityId: z.string(),
    samlAcsUrl: z.string().url(),
    samlSigningCertificate: z.string(),
    oidcDiscoveryUrl: z.string().url(),
    oidcClientId: z.string(),
    oidcClientSecret: z.string(),
    oidcScopes: z.array(z.string()),
    attributeMapping: z.object({}).partial().passthrough(),
    jitProvisioning: z.boolean(),
    enforceSSO: z.boolean(),
    defaultRoles: z.array(z.string()),
    metadata: z.object({}).partial().passthrough(),
})
    .partial()
    .passthrough();
const PatchOrgIdentitySSOProvider_Body = z
    .object({ action: z.literal("test") })
    .passthrough();
const UpdateOrgIdentitySSOSettings_Body = z
    .object({
    ssoEnabled: z.boolean(),
    enforceSSO: z.boolean(),
    allowedProviders: z.array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)),
    defaultProvider: z.union([z.string(), z.null()]),
    jitProvisioning: z.boolean(),
    jitProvisioningDomainWhitelist: z.array(z.string()),
    jitProvisioningDefaultRoles: z.array(z.string()),
})
    .partial()
    .passthrough();
const PostOrgsRequest = z
    .object({ name: z.string(), slug: z.string().optional() })
    .passthrough();
const PostOrgsResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            entityType: z.string().optional(),
            id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            orgId: z
                .string()
                .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                .optional(),
            name: z.string(),
            slug: z.string().optional(),
            status: z.enum(["active", "suspended", "disabled"]).optional(),
            domain: z.string().optional(),
            settings: z
                .object({
                allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
                requireMfa: z.boolean().default(false),
                sessionTimeoutMinutes: z.number().int(),
                maxSessionsPerUser: z.number().int(),
                timezone: z.string(),
                locale: z.string(),
                defaultCurrency: z.string(),
                featureFlags: z.record(z.boolean()),
                enabledSocialChannelSlugs: z.array(z.string()),
            })
                .partial()
                .passthrough()
                .optional(),
            metadata: z.object({}).partial().passthrough().optional(),
            createdAt: z.string().datetime({ offset: true }).optional(),
            updatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough()
        .and(z
        .object({
        pagination: z
            .object({
            nextCursor: z.union([z.string(), z.null()]).optional(),
            prevCursor: z.union([z.string(), z.null()]).optional(),
            limit: z.number().int().gte(1).lte(250).default(50),
        })
            .passthrough(),
    })
        .passthrough()),
})
    .passthrough();
const ListOrgsResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            entityType: z.string().optional(),
            id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            orgId: z
                .string()
                .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                .optional(),
            name: z.string(),
            slug: z.string().optional(),
            status: z.enum(["active", "suspended", "disabled"]).optional(),
            domain: z.string().optional(),
            settings: z
                .object({
                allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
                requireMfa: z.boolean().default(false),
                sessionTimeoutMinutes: z.number().int(),
                maxSessionsPerUser: z.number().int(),
                timezone: z.string(),
                locale: z.string(),
                defaultCurrency: z.string(),
                featureFlags: z.record(z.boolean()),
                enabledSocialChannelSlugs: z.array(z.string()),
            })
                .partial()
                .passthrough()
                .optional(),
            metadata: z.object({}).partial().passthrough().optional(),
            createdAt: z.string().datetime({ offset: true }).optional(),
            updatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough()
        .and(z
        .object({
        pagination: z
            .object({
            nextCursor: z.union([z.string(), z.null()]).optional(),
            prevCursor: z.union([z.string(), z.null()]).optional(),
            limit: z.number().int().gte(1).lte(250).default(50),
        })
            .passthrough(),
    })
        .passthrough()),
})
    .passthrough();
const GetOrgByIdResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        name: z.string(),
        slug: z.string().optional(),
        status: z.enum(["active", "suspended", "disabled"]).optional(),
        domain: z.string().optional(),
        settings: z
            .object({
            allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
            requireMfa: z.boolean().default(false),
            sessionTimeoutMinutes: z.number().int(),
            maxSessionsPerUser: z.number().int(),
            timezone: z.string(),
            locale: z.string(),
            defaultCurrency: z.string(),
            featureFlags: z.record(z.boolean()),
            enabledSocialChannelSlugs: z.array(z.string()),
        })
            .partial()
            .passthrough()
            .optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const UpdateOrgRequest = z
    .object({
    name: z.string(),
    slug: z.string(),
    status: z.enum(["active", "suspended", "disabled"]),
    domain: z.string(),
    settings: z
        .object({
        allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
        requireMfa: z.boolean().default(false),
        sessionTimeoutMinutes: z.number().int(),
        maxSessionsPerUser: z.number().int(),
        timezone: z.string(),
        locale: z.string(),
        defaultCurrency: z.string(),
        featureFlags: z.record(z.boolean()),
        enabledSocialChannelSlugs: z.array(z.string()),
    })
        .partial()
        .passthrough(),
    metadata: z.object({}).partial().passthrough(),
})
    .partial()
    .passthrough();
const UpdateOrgByIdResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        name: z.string(),
        slug: z.string().optional(),
        status: z.enum(["active", "suspended", "disabled"]).optional(),
        domain: z.string().optional(),
        settings: z
            .object({
            allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
            requireMfa: z.boolean().default(false),
            sessionTimeoutMinutes: z.number().int(),
            maxSessionsPerUser: z.number().int(),
            timezone: z.string(),
            locale: z.string(),
            defaultCurrency: z.string(),
            featureFlags: z.record(z.boolean()),
            enabledSocialChannelSlugs: z.array(z.string()),
        })
            .partial()
            .passthrough()
            .optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const PostAuthSessionsRequest = z
    .object({ email: z.string().email(), password: z.string() })
    .passthrough();
const PostAuthSessionsResponse = z
    .object({
    data: z
        .object({
        accessToken: z.string(),
        refreshToken: z.string().optional(),
        orgIds: z.array(z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)),
        userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        email: z.string().email(),
        role: z.string(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const RefreshAuthSessionRequest = z
    .object({ refreshToken: z.string() })
    .passthrough();
const RefreshAuthSessionResponse = z
    .object({
    data: z
        .object({ accessToken: z.string(), refreshToken: z.string() })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const PasswordResetRequest = z
    .object({ email: z.string().email() })
    .passthrough();
const PasswordResetResponse = z
    .object({ message: z.string() })
    .partial()
    .passthrough();
const UserPasswordResetRequest = z.object({}).partial().passthrough();
const UserPasswordResetResponse = z
    .object({ message: z.string() })
    .partial()
    .passthrough();
const ListSessionsResponse = z
    .object({
    items: z.array(z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z.string().optional(),
        userId: z.string().optional(),
        status: z.enum(["active", "expired", "revoked"]).optional(),
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
        deviceType: z.string().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        lastActivityAt: z.string().datetime({ offset: true }).optional(),
        expiresAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough()),
    nextToken: z.string().optional(),
})
    .passthrough();
const GetSessionResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z.string().optional(),
        userId: z.string().optional(),
        status: z.enum(["active", "expired", "revoked"]).optional(),
        ipAddress: z.string().optional(),
        userAgent: z.string().optional(),
        deviceType: z.string().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        lastActivityAt: z.string().datetime({ offset: true }).optional(),
        expiresAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const CompleteUserOnboardingStepRequest = z
    .object({ stepId: z.string() })
    .passthrough();
const CompleteUserOnboardingStepResponse = z
    .object({
    data: z
        .object({
        success: z.boolean(),
        stepId: z.string(),
        completed: z.boolean(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const CreateSSOProviderRequest = z
    .object({
    providerType: z.enum(["saml", "oidc", "oauth2"]),
    providerId: z.string(),
    name: z.string().optional(),
    description: z.string().optional(),
    enabled: z.boolean().optional().default(true),
    samlMetadataUrl: z.string().url().optional(),
    samlMetadataXml: z.string().optional(),
    samlEntityId: z.string().optional(),
    samlAcsUrl: z.string().url().optional(),
    samlSigningCertificate: z.string().optional(),
    oidcDiscoveryUrl: z.string().url().optional(),
    oidcClientId: z.string().optional(),
    oidcClientSecret: z.string().optional(),
    oidcScopes: z.array(z.string()).optional(),
    attributeMapping: z.object({}).partial().passthrough().optional(),
    jitProvisioning: z.boolean().optional().default(false),
    enforceSSO: z.boolean().optional().default(false),
    defaultRoles: z.array(z.string()).optional(),
    metadata: z.object({}).partial().passthrough().optional(),
})
    .passthrough();
const CreateSSOProviderResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoProviderId: z
            .string()
            .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        providerType: z.enum(["saml", "oidc", "oauth2"]),
        providerId: z.string(),
        enabled: z.boolean().default(true),
        name: z.string().optional(),
        description: z.string().optional(),
        samlMetadataUrl: z.string().url().optional(),
        samlMetadataXml: z.string().optional(),
        samlEntityId: z.string().optional(),
        samlAcsUrl: z.string().url().optional(),
        samlSigningCertificate: z.string().optional(),
        oidcDiscoveryUrl: z.string().url().optional(),
        oidcClientId: z.string().optional(),
        oidcClientSecret: z.string().optional(),
        oidcScopes: z.array(z.string()).optional(),
        attributeMapping: z
            .object({
            email: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            displayName: z.string(),
            roles: z.string(),
            groups: z.string(),
        })
            .partial()
            .passthrough()
            .optional(),
        jitProvisioning: z.boolean().optional().default(false),
        enforceSSO: z.boolean().optional().default(false),
        defaultRoles: z.array(z.string()).optional(),
        configuration: z
            .object({
            issuerUrl: z.string().url(),
            clientId: z.string(),
            scopes: z.array(z.string()),
            attributeMapping: z
                .object({
                email: z.string(),
                name: z.string(),
                firstName: z.string(),
                lastName: z.string(),
            })
                .partial()
                .passthrough(),
        })
            .partial()
            .passthrough()
            .optional(),
        domainRestriction: z.array(z.string()).optional(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetSSOProviderResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoProviderId: z
            .string()
            .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        providerType: z.enum(["saml", "oidc", "oauth2"]),
        providerId: z.string(),
        enabled: z.boolean().default(true),
        name: z.string().optional(),
        description: z.string().optional(),
        samlMetadataUrl: z.string().url().optional(),
        samlMetadataXml: z.string().optional(),
        samlEntityId: z.string().optional(),
        samlAcsUrl: z.string().url().optional(),
        samlSigningCertificate: z.string().optional(),
        oidcDiscoveryUrl: z.string().url().optional(),
        oidcClientId: z.string().optional(),
        oidcClientSecret: z.string().optional(),
        oidcScopes: z.array(z.string()).optional(),
        attributeMapping: z
            .object({
            email: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            displayName: z.string(),
            roles: z.string(),
            groups: z.string(),
        })
            .partial()
            .passthrough()
            .optional(),
        jitProvisioning: z.boolean().optional().default(false),
        enforceSSO: z.boolean().optional().default(false),
        defaultRoles: z.array(z.string()).optional(),
        configuration: z
            .object({
            issuerUrl: z.string().url(),
            clientId: z.string(),
            scopes: z.array(z.string()),
            attributeMapping: z
                .object({
                email: z.string(),
                name: z.string(),
                firstName: z.string(),
                lastName: z.string(),
            })
                .partial()
                .passthrough(),
        })
            .partial()
            .passthrough()
            .optional(),
        domainRestriction: z.array(z.string()).optional(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetSSOSettingsResponse = z
    .object({
    data: z
        .object({
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoEnabled: z.boolean().default(false),
        enforceSSO: z.boolean().optional().default(false),
        allowedProviders: z
            .array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/))
            .optional(),
        defaultProvider: z.union([z.string(), z.null()]).optional(),
        jitProvisioning: z.boolean().optional().default(false),
        jitProvisioningDomainWhitelist: z.array(z.string()).optional(),
        jitProvisioningDefaultRoles: z.array(z.string()).optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetUserOnboardingResponse = z
    .object({
    data: z
        .object({ accessToken: z.string(), refreshToken: z.string() })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const ListOrgIdentityOnboardingStatusesResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            userName: z.string().optional(),
            userEmail: z.string().optional(),
            completed: z.boolean(),
            steps: z.array(z
                .object({
                id: z.string(),
                name: z.string(),
                completed: z.boolean(),
                completedAt: z
                    .string()
                    .datetime({ offset: true })
                    .optional(),
            })
                .passthrough()),
            currentStep: z.string().optional(),
            completionPercentage: z.number().gte(0).lte(100),
            onboardingStartedAt: z
                .string()
                .datetime({ offset: true })
                .optional(),
            onboardingCompletedAt: z
                .string()
                .datetime({ offset: true })
                .optional(),
            lastActivityAt: z.string().datetime({ offset: true }).optional(),
            userCreatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough()
        .and(z
        .object({
        pagination: z
            .object({
            nextCursor: z.union([z.string(), z.null()]).optional(),
            prevCursor: z.union([z.string(), z.null()]).optional(),
            limit: z.number().int().gte(1).lte(250).default(50),
        })
            .passthrough(),
    })
        .passthrough()),
})
    .passthrough();
const OrgIdentityOnboardingStatus = z
    .object({
    userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    userName: z.string().optional(),
    userEmail: z.string().optional(),
    completed: z.boolean(),
    steps: z.array(z
        .object({
        id: z.string(),
        name: z.string(),
        completed: z.boolean(),
        completedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough()),
    currentStep: z.string().optional(),
    completionPercentage: z.number().gte(0).lte(100),
    onboardingStartedAt: z.string().datetime({ offset: true }).optional(),
    onboardingCompletedAt: z.string().datetime({ offset: true }).optional(),
    lastActivityAt: z.string().datetime({ offset: true }).optional(),
    userCreatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const UserOnboarding = z
    .object({
    data: z
        .object({
        success: z.boolean(),
        stepId: z.string(),
        completed: z.boolean(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetUserResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        userId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        email: z.string().email(),
        emailVerified: z.boolean().optional(),
        displayName: z.string(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        avatar: z.union([z.string(), z.null()]).optional(),
        avatarS3Key: z.union([z.string(), z.null()]).optional(),
        coverS3Key: z.union([z.string(), z.null()]).optional(),
        phone: z.union([z.string(), z.null()]).optional(),
        phoneVerified: z.boolean().optional().default(false),
        roles: z
            .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
            .optional(),
        role: z.string().optional(),
        permissions: z.array(z.string()).optional(),
        status: z.enum([
            "active",
            "suspended",
            "invited",
            "deactivated",
            "pending_verification",
            "disabled",
        ]),
        profile: z
            .object({
            department: z.string(),
            title: z.string(),
            timezone: z.string(),
            locale: z.string(),
        })
            .partial()
            .passthrough()
            .optional(),
        authentication: z
            .object({
            lastLoginAt: z.union([z.string(), z.null()]),
            mfaEnrolled: z.boolean(),
            mfaMethod: z.union([z.string(), z.null()]),
            passwordChangedAt: z.union([z.string(), z.null()]),
        })
            .partial()
            .passthrough()
            .optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        tags: z.array(z.string()).optional(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        updatedBy: z.union([z.string(), z.null()]).optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const ListRolesResponse = z
    .object({
    items: z.array(z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        name: z.string(),
        slug: z.string().optional(),
        description: z.string().optional(),
        permissions: z.array(z.string()).optional(),
        isSystem: z.boolean().optional().default(false),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough()),
    nextToken: z.string().optional(),
})
    .passthrough();
const CreateRoleRequest = z
    .object({
    name: z.string(),
    description: z.string().optional(),
    permissions: z.array(z.string()).optional(),
})
    .passthrough();
const CreateRoleResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        name: z.string(),
        slug: z.string().optional(),
        description: z.string().optional(),
        permissions: z.array(z.string()).optional(),
        isSystem: z.boolean().optional().default(false),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetRoleResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        name: z.string(),
        slug: z.string().optional(),
        description: z.string().optional(),
        permissions: z.array(z.string()).optional(),
        isSystem: z.boolean().optional().default(false),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const UpdateRoleRequest = z
    .object({
    name: z.string(),
    description: z.string(),
    permissions: z.array(z.string()),
})
    .partial()
    .passthrough();
const UpdateRoleResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        orgId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        name: z.string(),
        slug: z.string().optional(),
        description: z.string().optional(),
        permissions: z.array(z.string()).optional(),
        isSystem: z.boolean().optional().default(false),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const CreateApiKeyRequest = z
    .object({
    name: z.string(),
    scopes: z
        .array(z.enum([
        "mcp:invoke",
        "mcp:admin",
        "content:read",
        "content:write",
        "publishing:read",
        "publishing:write",
        "publishing:dispatch",
        "social:read",
        "social:write",
        "social:reply",
        "connectivity:read",
        "connectivity:write",
        "focus:read",
        "focus:write",
        "analytics:read",
        "knowledge:read",
        "knowledge:write",
    ]))
        .optional(),
    expiresAt: z.union([z.string(), z.null()]).optional(),
    environment: z.enum(["live", "test"]).optional().default("live"),
})
    .passthrough();
const CreateApiKeyResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
        keyPrefix: z.string(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        userId: z.string().optional(),
        name: z.string().optional(),
        status: z.enum(["active", "revoked", "expired"]),
        scopes: z
            .array(z.enum([
            "mcp:invoke",
            "mcp:admin",
            "content:read",
            "content:write",
            "publishing:read",
            "publishing:write",
            "publishing:dispatch",
            "social:read",
            "social:write",
            "social:reply",
            "connectivity:read",
            "connectivity:write",
            "focus:read",
            "focus:write",
            "analytics:read",
            "knowledge:read",
            "knowledge:write",
        ]))
            .optional(),
        lastUsedAt: z.string().datetime({ offset: true }).optional(),
        expiresAt: z.union([z.string(), z.null()]).optional(),
        createdBy: z.string().optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough()
        .and(z.object({ secret: z.string() }).passthrough()),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetApiKeyResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
        keyPrefix: z.string(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        userId: z.string().optional(),
        name: z.string().optional(),
        status: z.enum(["active", "revoked", "expired"]),
        scopes: z
            .array(z.enum([
            "mcp:invoke",
            "mcp:admin",
            "content:read",
            "content:write",
            "publishing:read",
            "publishing:write",
            "publishing:dispatch",
            "social:read",
            "social:write",
            "social:reply",
            "connectivity:read",
            "connectivity:write",
            "focus:read",
            "focus:write",
            "analytics:read",
            "knowledge:read",
            "knowledge:write",
        ]))
            .optional(),
        lastUsedAt: z.string().datetime({ offset: true }).optional(),
        expiresAt: z.union([z.string(), z.null()]).optional(),
        createdBy: z.string().optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const ListApiKeysResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            entityType: z.string().optional(),
            id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
            keyPrefix: z.string(),
            orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            userId: z.string().optional(),
            name: z.string().optional(),
            status: z.enum(["active", "revoked", "expired"]),
            scopes: z
                .array(z.enum([
                "mcp:invoke",
                "mcp:admin",
                "content:read",
                "content:write",
                "publishing:read",
                "publishing:write",
                "publishing:dispatch",
                "social:read",
                "social:write",
                "social:reply",
                "connectivity:read",
                "connectivity:write",
                "focus:read",
                "focus:write",
                "analytics:read",
                "knowledge:read",
                "knowledge:write",
            ]))
                .optional(),
            lastUsedAt: z.string().datetime({ offset: true }).optional(),
            expiresAt: z.union([z.string(), z.null()]).optional(),
            createdBy: z.string().optional(),
            metadata: z.object({}).partial().passthrough().optional(),
            createdAt: z.string().datetime({ offset: true }).optional(),
            updatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
        nextToken: z.string().optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const RevokeApiKeyResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
        keyPrefix: z.string(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        userId: z.string().optional(),
        name: z.string().optional(),
        status: z.enum(["active", "revoked", "expired"]),
        scopes: z
            .array(z.enum([
            "mcp:invoke",
            "mcp:admin",
            "content:read",
            "content:write",
            "publishing:read",
            "publishing:write",
            "publishing:dispatch",
            "social:read",
            "social:write",
            "social:reply",
            "connectivity:read",
            "connectivity:write",
            "focus:read",
            "focus:write",
            "analytics:read",
            "knowledge:read",
            "knowledge:write",
        ]))
            .optional(),
        lastUsedAt: z.string().datetime({ offset: true }).optional(),
        expiresAt: z.union([z.string(), z.null()]).optional(),
        createdBy: z.string().optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const ApiKey = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
    keyPrefix: z.string(),
    keyHash: z.string().optional(),
    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    userId: z.string().optional(),
    name: z.string().optional(),
    status: z.enum(["active", "revoked", "expired"]),
    scopes: z
        .array(z.enum([
        "mcp:invoke",
        "mcp:admin",
        "content:read",
        "content:write",
        "publishing:read",
        "publishing:write",
        "publishing:dispatch",
        "social:read",
        "social:write",
        "social:reply",
        "connectivity:read",
        "connectivity:write",
        "focus:read",
        "focus:write",
        "analytics:read",
        "knowledge:read",
        "knowledge:write",
    ]))
        .optional(),
    lastUsedAt: z.string().datetime({ offset: true }).optional(),
    expiresAt: z.union([z.string(), z.null()]).optional(),
    createdBy: z.string().optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const ApiKeyPublic = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
    keyPrefix: z.string(),
    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    userId: z.string().optional(),
    name: z.string().optional(),
    status: z.enum(["active", "revoked", "expired"]),
    scopes: z
        .array(z.enum([
        "mcp:invoke",
        "mcp:admin",
        "content:read",
        "content:write",
        "publishing:read",
        "publishing:write",
        "publishing:dispatch",
        "social:read",
        "social:write",
        "social:reply",
        "connectivity:read",
        "connectivity:write",
        "focus:read",
        "focus:write",
        "analytics:read",
        "knowledge:read",
        "knowledge:write",
    ]))
        .optional(),
    lastUsedAt: z.string().datetime({ offset: true }).optional(),
    expiresAt: z.union([z.string(), z.null()]).optional(),
    createdBy: z.string().optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const ApiKeyScope = z.enum([
    "mcp:invoke",
    "mcp:admin",
    "content:read",
    "content:write",
    "publishing:read",
    "publishing:write",
    "publishing:dispatch",
    "social:read",
    "social:write",
    "social:reply",
    "connectivity:read",
    "connectivity:write",
    "focus:read",
    "focus:write",
    "analytics:read",
    "knowledge:read",
    "knowledge:write",
]);
const ListSSOProvidersResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            entityType: z.string().optional(),
            id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
            ssoProviderId: z
                .string()
                .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
                .optional(),
            orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            providerType: z.enum(["saml", "oidc", "oauth2"]),
            providerId: z.string(),
            enabled: z.boolean().default(true),
            name: z.string().optional(),
            description: z.string().optional(),
            samlMetadataUrl: z.string().url().optional(),
            samlMetadataXml: z.string().optional(),
            samlEntityId: z.string().optional(),
            samlAcsUrl: z.string().url().optional(),
            samlSigningCertificate: z.string().optional(),
            oidcDiscoveryUrl: z.string().url().optional(),
            oidcClientId: z.string().optional(),
            oidcClientSecret: z.string().optional(),
            oidcScopes: z.array(z.string()).optional(),
            attributeMapping: z
                .object({
                email: z.string(),
                firstName: z.string(),
                lastName: z.string(),
                displayName: z.string(),
                roles: z.string(),
                groups: z.string(),
            })
                .partial()
                .passthrough()
                .optional(),
            jitProvisioning: z.boolean().optional().default(false),
            enforceSSO: z.boolean().optional().default(false),
            defaultRoles: z.array(z.string()).optional(),
            configuration: z
                .object({
                issuerUrl: z.string().url(),
                clientId: z.string(),
                scopes: z.array(z.string()),
                attributeMapping: z
                    .object({
                    email: z.string(),
                    name: z.string(),
                    firstName: z.string(),
                    lastName: z.string(),
                })
                    .partial()
                    .passthrough(),
            })
                .partial()
                .passthrough()
                .optional(),
            domainRestriction: z.array(z.string()).optional(),
            createdBy: z.union([z.string(), z.null()]).optional(),
            metadata: z.object({}).partial().passthrough().optional(),
            createdAt: z.string().datetime({ offset: true }).optional(),
            updatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough()
        .and(z
        .object({
        pagination: z
            .object({
            nextCursor: z.union([z.string(), z.null()]).optional(),
            prevCursor: z.union([z.string(), z.null()]).optional(),
            limit: z.number().int().gte(1).lte(250).default(50),
        })
            .passthrough(),
    })
        .passthrough()),
})
    .passthrough();
const ListUsersResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            entityType: z.string().optional(),
            id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            userId: z
                .string()
                .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                .optional(),
            orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            email: z.string().email(),
            emailVerified: z.boolean().optional(),
            displayName: z.string(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            avatar: z.union([z.string(), z.null()]).optional(),
            avatarS3Key: z.union([z.string(), z.null()]).optional(),
            coverS3Key: z.union([z.string(), z.null()]).optional(),
            phone: z.union([z.string(), z.null()]).optional(),
            phoneVerified: z.boolean().optional().default(false),
            roles: z
                .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
                .optional(),
            role: z.string().optional(),
            permissions: z.array(z.string()).optional(),
            status: z.enum([
                "active",
                "suspended",
                "invited",
                "deactivated",
                "pending_verification",
                "disabled",
            ]),
            profile: z
                .object({
                department: z.string(),
                title: z.string(),
                timezone: z.string(),
                locale: z.string(),
            })
                .partial()
                .passthrough()
                .optional(),
            authentication: z
                .object({
                lastLoginAt: z.union([z.string(), z.null()]),
                mfaEnrolled: z.boolean(),
                mfaMethod: z.union([z.string(), z.null()]),
                passwordChangedAt: z.union([z.string(), z.null()]),
            })
                .partial()
                .passthrough()
                .optional(),
            metadata: z.object({}).partial().passthrough().optional(),
            tags: z.array(z.string()).optional(),
            createdBy: z.union([z.string(), z.null()]).optional(),
            updatedBy: z.union([z.string(), z.null()]).optional(),
            createdAt: z.string().datetime({ offset: true }).optional(),
            updatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough()
        .and(z
        .object({
        pagination: z
            .object({
            nextCursor: z.union([z.string(), z.null()]).optional(),
            prevCursor: z.union([z.string(), z.null()]).optional(),
            limit: z.number().int().gte(1).lte(250).default(50),
        })
            .passthrough(),
    })
        .passthrough()),
})
    .passthrough();
const PostUsersRequest = z
    .object({
    email: z.string().email(),
    displayName: z.string().optional(),
    roles: z
        .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
        .optional(),
})
    .passthrough();
const PostUsersResponse = z
    .object({
    data: z
        .object({
        items: z.array(z
            .object({
            entityType: z.string().optional(),
            id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            userId: z
                .string()
                .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                .optional(),
            orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            email: z.string().email(),
            emailVerified: z.boolean().optional(),
            displayName: z.string(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            avatar: z.union([z.string(), z.null()]).optional(),
            avatarS3Key: z.union([z.string(), z.null()]).optional(),
            coverS3Key: z.union([z.string(), z.null()]).optional(),
            phone: z.union([z.string(), z.null()]).optional(),
            phoneVerified: z.boolean().optional().default(false),
            roles: z
                .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
                .optional(),
            role: z.string().optional(),
            permissions: z.array(z.string()).optional(),
            status: z.enum([
                "active",
                "suspended",
                "invited",
                "deactivated",
                "pending_verification",
                "disabled",
            ]),
            profile: z
                .object({
                department: z.string(),
                title: z.string(),
                timezone: z.string(),
                locale: z.string(),
            })
                .partial()
                .passthrough()
                .optional(),
            authentication: z
                .object({
                lastLoginAt: z.union([z.string(), z.null()]),
                mfaEnrolled: z.boolean(),
                mfaMethod: z.union([z.string(), z.null()]),
                passwordChangedAt: z.union([z.string(), z.null()]),
            })
                .partial()
                .passthrough()
                .optional(),
            metadata: z.object({}).partial().passthrough().optional(),
            tags: z.array(z.string()).optional(),
            createdBy: z.union([z.string(), z.null()]).optional(),
            updatedBy: z.union([z.string(), z.null()]).optional(),
            createdAt: z.string().datetime({ offset: true }).optional(),
            updatedAt: z.string().datetime({ offset: true }).optional(),
        })
            .passthrough()),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough()
        .and(z
        .object({
        pagination: z
            .object({
            nextCursor: z.union([z.string(), z.null()]).optional(),
            prevCursor: z.union([z.string(), z.null()]).optional(),
            limit: z.number().int().gte(1).lte(250).default(50),
        })
            .passthrough(),
    })
        .passthrough()),
})
    .passthrough();
const Problem = z
    .object({
    type: z.string().url(),
    title: z.string(),
    status: z.number().int().gte(100).lte(599),
    detail: z.string().optional(),
    instance: z.string().optional(),
    code: z.string().optional(),
    traceId: z.string().optional(),
    errors: z
        .array(z
        .object({
        field: z.string(),
        message: z.string(),
        code: z.string().optional(),
    })
        .passthrough())
        .optional(),
})
    .passthrough();
const TestSSOProviderResponse = z
    .object({
    data: z
        .object({
        success: z.boolean(),
        errors: z
            .array(z
            .object({ code: z.string(), message: z.string() })
            .partial()
            .passthrough())
            .optional(),
        warnings: z
            .array(z
            .object({ code: z.string(), message: z.string() })
            .partial()
            .passthrough())
            .optional(),
        metadataValid: z.boolean().optional(),
        connectionValid: z.boolean().optional(),
        certificateValid: z.boolean().optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const UpdateOrgSettingsRequest = z
    .object({
    name: z.string(),
    slug: z.string(),
    status: z.enum(["active", "suspended", "disabled"]),
    domain: z.string(),
    settings: z
        .object({
        allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
        requireMfa: z.boolean().default(false),
        sessionTimeoutMinutes: z.number().int(),
        maxSessionsPerUser: z.number().int(),
        timezone: z.string(),
        locale: z.string(),
        defaultCurrency: z.string(),
        featureFlags: z.record(z.boolean()),
        enabledSocialChannelSlugs: z.array(z.string()),
    })
        .partial()
        .passthrough(),
    metadata: z.object({}).partial().passthrough(),
})
    .partial()
    .passthrough();
const PatchSSOProviderRequest = z
    .object({ action: z.literal("test") })
    .passthrough();
const UpdateSSOProviderRequest = z
    .object({
    name: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    samlMetadataUrl: z.string().url(),
    samlMetadataXml: z.string(),
    samlEntityId: z.string(),
    samlAcsUrl: z.string().url(),
    samlSigningCertificate: z.string(),
    oidcDiscoveryUrl: z.string().url(),
    oidcClientId: z.string(),
    oidcClientSecret: z.string(),
    oidcScopes: z.array(z.string()),
    attributeMapping: z.object({}).partial().passthrough(),
    jitProvisioning: z.boolean(),
    enforceSSO: z.boolean(),
    defaultRoles: z.array(z.string()),
    metadata: z.object({}).partial().passthrough(),
})
    .partial()
    .passthrough();
const UpdateSSOProviderResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoProviderId: z
            .string()
            .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        providerType: z.enum(["saml", "oidc", "oauth2"]),
        providerId: z.string(),
        enabled: z.boolean().default(true),
        name: z.string().optional(),
        description: z.string().optional(),
        samlMetadataUrl: z.string().url().optional(),
        samlMetadataXml: z.string().optional(),
        samlEntityId: z.string().optional(),
        samlAcsUrl: z.string().url().optional(),
        samlSigningCertificate: z.string().optional(),
        oidcDiscoveryUrl: z.string().url().optional(),
        oidcClientId: z.string().optional(),
        oidcClientSecret: z.string().optional(),
        oidcScopes: z.array(z.string()).optional(),
        attributeMapping: z
            .object({
            email: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            displayName: z.string(),
            roles: z.string(),
            groups: z.string(),
        })
            .partial()
            .passthrough()
            .optional(),
        jitProvisioning: z.boolean().optional().default(false),
        enforceSSO: z.boolean().optional().default(false),
        defaultRoles: z.array(z.string()).optional(),
        configuration: z
            .object({
            issuerUrl: z.string().url(),
            clientId: z.string(),
            scopes: z.array(z.string()),
            attributeMapping: z
                .object({
                email: z.string(),
                name: z.string(),
                firstName: z.string(),
                lastName: z.string(),
            })
                .partial()
                .passthrough(),
        })
            .partial()
            .passthrough()
            .optional(),
        domainRestriction: z.array(z.string()).optional(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const UpdateSSOSettingsRequest = z
    .object({
    ssoEnabled: z.boolean(),
    enforceSSO: z.boolean(),
    allowedProviders: z.array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)),
    defaultProvider: z.union([z.string(), z.null()]),
    jitProvisioning: z.boolean(),
    jitProvisioningDomainWhitelist: z.array(z.string()),
    jitProvisioningDefaultRoles: z.array(z.string()),
})
    .partial()
    .passthrough();
const UpdateSSOSettingsResponse = z
    .object({
    data: z
        .object({
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoEnabled: z.boolean().default(false),
        enforceSSO: z.boolean().optional().default(false),
        allowedProviders: z
            .array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/))
            .optional(),
        defaultProvider: z.union([z.string(), z.null()]).optional(),
        jitProvisioning: z.boolean().optional().default(false),
        jitProvisioningDomainWhitelist: z.array(z.string()).optional(),
        jitProvisioningDefaultRoles: z.array(z.string()).optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const UpdateUserRequest = z
    .object({
    displayName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: z.union([z.string(), z.null()]),
    phone: z.union([z.string(), z.null()]),
    roles: z.array(z.enum(["platform_admin", "org_admin", "user", "viewer"])),
    role: z.string(),
    status: z.enum([
        "active",
        "suspended",
        "invited",
        "deactivated",
        "pending_verification",
        "disabled",
    ]),
    profile: z
        .object({
        department: z.string(),
        title: z.string(),
        timezone: z.string(),
        locale: z.string(),
    })
        .partial()
        .passthrough(),
    metadata: z.object({}).partial().passthrough(),
    tags: z.array(z.string()),
})
    .partial()
    .passthrough();
const UpdateUserResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        userId: z
            .string()
            .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        email: z.string().email(),
        emailVerified: z.boolean().optional(),
        displayName: z.string(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        avatar: z.union([z.string(), z.null()]).optional(),
        avatarS3Key: z.union([z.string(), z.null()]).optional(),
        coverS3Key: z.union([z.string(), z.null()]).optional(),
        phone: z.union([z.string(), z.null()]).optional(),
        phoneVerified: z.boolean().optional().default(false),
        roles: z
            .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
            .optional(),
        role: z.string().optional(),
        permissions: z.array(z.string()).optional(),
        status: z.enum([
            "active",
            "suspended",
            "invited",
            "deactivated",
            "pending_verification",
            "disabled",
        ]),
        profile: z
            .object({
            department: z.string(),
            title: z.string(),
            timezone: z.string(),
            locale: z.string(),
        })
            .partial()
            .passthrough()
            .optional(),
        authentication: z
            .object({
            lastLoginAt: z.union([z.string(), z.null()]),
            mfaEnrolled: z.boolean(),
            mfaMethod: z.union([z.string(), z.null()]),
            passwordChangedAt: z.union([z.string(), z.null()]),
        })
            .partial()
            .passthrough()
            .optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        tags: z.array(z.string()).optional(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        updatedBy: z.union([z.string(), z.null()]).optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const IdentityProfile = z
    .object({
    userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    email: z.string().email(),
    displayName: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    avatarUrl: z.union([z.string(), z.null()]).optional(),
    coverUrl: z.union([z.string(), z.null()]).optional(),
    profile: z
        .object({ timezone: z.string(), locale: z.string() })
        .partial()
        .passthrough()
        .optional(),
})
    .passthrough();
const UpdateIdentityProfileRequest = z
    .object({
    displayName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    profile: z
        .object({ timezone: z.string(), locale: z.string() })
        .partial()
        .passthrough(),
})
    .partial()
    .passthrough();
const GetIdentityProfileResponse = z
    .object({
    data: z
        .object({
        userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        email: z.string().email(),
        displayName: z.string().optional(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        avatarUrl: z.union([z.string(), z.null()]).optional(),
        coverUrl: z.union([z.string(), z.null()]).optional(),
        profile: z
            .object({ timezone: z.string(), locale: z.string() })
            .partial()
            .passthrough()
            .optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const GetCurrentIdentityResponse = z
    .object({
    data: z
        .object({
        orgId: z.string(),
        scopes: z.array(z.string()).optional(),
        apiKeyId: z.string().optional(),
        userId: z.string().optional(),
        authType: z.string().optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const UploadIdentityProfileMediaRequest = z
    .object({ file: z.instanceof(File) })
    .passthrough();
const ResponseMeta = z
    .object({
    correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
    timestamp: z.string().datetime({ offset: true }),
    requestId: z.string().optional(),
})
    .passthrough();
const CursorQueryParam = z.string();
const LimitQueryParamHigh = z.number();
const OrgId = z.string();
const OrgSettings = z
    .object({
    allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
    requireMfa: z.boolean().default(false),
    sessionTimeoutMinutes: z.number().int(),
    maxSessionsPerUser: z.number().int(),
    timezone: z.string(),
    locale: z.string(),
    defaultCurrency: z.string(),
    featureFlags: z.record(z.boolean()),
    enabledSocialChannelSlugs: z.array(z.string()),
})
    .partial()
    .passthrough();
const Org = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    orgId: z
        .string()
        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
        .optional(),
    name: z.string(),
    slug: z.string().optional(),
    status: z.enum(["active", "suspended", "disabled"]).optional(),
    domain: z.string().optional(),
    settings: z
        .object({
        allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
        requireMfa: z.boolean().default(false),
        sessionTimeoutMinutes: z.number().int(),
        maxSessionsPerUser: z.number().int(),
        timezone: z.string(),
        locale: z.string(),
        defaultCurrency: z.string(),
        featureFlags: z.record(z.boolean()),
        enabledSocialChannelSlugs: z.array(z.string()),
    })
        .partial()
        .passthrough()
        .optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const PageInfo = z
    .object({
    nextCursor: z.union([z.string(), z.null()]).optional(),
    prevCursor: z.union([z.string(), z.null()]).optional(),
    limit: z.number().int().gte(1).lte(250).default(50),
})
    .passthrough();
const CreateOrgRequest = z
    .object({ name: z.string(), slug: z.string().optional() })
    .passthrough();
const OrgIdPathParam = z.string();
const LoginRequest = z
    .object({ email: z.string().email(), password: z.string() })
    .passthrough();
const LoginPayload = z
    .object({
    accessToken: z.string(),
    refreshToken: z.string().optional(),
    orgIds: z.array(z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)),
    userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    email: z.string().email(),
    role: z.string(),
})
    .passthrough();
const LoginResponse = z
    .object({
    data: z
        .object({
        accessToken: z.string(),
        refreshToken: z.string().optional(),
        orgIds: z.array(z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)),
        userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        email: z.string().email(),
        role: z.string(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const RefreshRequest = z.object({ refreshToken: z.string() }).passthrough();
const RefreshPayload = z
    .object({ accessToken: z.string(), refreshToken: z.string() })
    .passthrough();
const Role = z.enum(["platform_admin", "org_admin", "user", "viewer"]);
const User = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    userId: z
        .string()
        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
        .optional(),
    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    email: z.string().email(),
    emailVerified: z.boolean().optional(),
    displayName: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    avatar: z.union([z.string(), z.null()]).optional(),
    avatarS3Key: z.union([z.string(), z.null()]).optional(),
    coverS3Key: z.union([z.string(), z.null()]).optional(),
    phone: z.union([z.string(), z.null()]).optional(),
    phoneVerified: z.boolean().optional().default(false),
    roles: z
        .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
        .optional(),
    role: z.string().optional(),
    permissions: z.array(z.string()).optional(),
    status: z.enum([
        "active",
        "suspended",
        "invited",
        "deactivated",
        "pending_verification",
        "disabled",
    ]),
    profile: z
        .object({
        department: z.string(),
        title: z.string(),
        timezone: z.string(),
        locale: z.string(),
    })
        .partial()
        .passthrough()
        .optional(),
    authentication: z
        .object({
        lastLoginAt: z.union([z.string(), z.null()]),
        mfaEnrolled: z.boolean(),
        mfaMethod: z.union([z.string(), z.null()]),
        passwordChangedAt: z.union([z.string(), z.null()]),
    })
        .partial()
        .passthrough()
        .optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    tags: z.array(z.string()).optional(),
    createdBy: z.union([z.string(), z.null()]).optional(),
    updatedBy: z.union([z.string(), z.null()]).optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const CreateUserRequest = z
    .object({
    email: z.string().email(),
    displayName: z.string().optional(),
    roles: z
        .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
        .optional(),
})
    .passthrough();
const UserIdPathParam = z.string();
const CompleteOnboardingStepRequest = z
    .object({ stepId: z.string() })
    .passthrough();
const GetOnboardingStatusResponse = z
    .object({
    data: z
        .object({
        success: z.boolean(),
        stepId: z.string(),
        completed: z.boolean(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const OnboardingStep = z
    .object({
    id: z.string(),
    name: z.string(),
    completed: z.boolean(),
    completedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const SessionEntity = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    orgId: z.string().optional(),
    userId: z.string().optional(),
    status: z.enum(["active", "expired", "revoked"]).optional(),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    deviceType: z.string().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    lastActivityAt: z.string().datetime({ offset: true }).optional(),
    expiresAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const RoleEntity = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    orgId: z
        .string()
        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
        .optional(),
    name: z.string(),
    slug: z.string().optional(),
    description: z.string().optional(),
    permissions: z.array(z.string()).optional(),
    isSystem: z.boolean().optional().default(false),
    createdBy: z.union([z.string(), z.null()]).optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const CreateApiKeyData = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
    keyPrefix: z.string(),
    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    userId: z.string().optional(),
    name: z.string().optional(),
    status: z.enum(["active", "revoked", "expired"]),
    scopes: z
        .array(z.enum([
        "mcp:invoke",
        "mcp:admin",
        "content:read",
        "content:write",
        "publishing:read",
        "publishing:write",
        "publishing:dispatch",
        "social:read",
        "social:write",
        "social:reply",
        "connectivity:read",
        "connectivity:write",
        "focus:read",
        "focus:write",
        "analytics:read",
        "knowledge:read",
        "knowledge:write",
    ]))
        .optional(),
    lastUsedAt: z.string().datetime({ offset: true }).optional(),
    expiresAt: z.union([z.string(), z.null()]).optional(),
    createdBy: z.string().optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough()
    .and(z.object({ secret: z.string() }).passthrough());
const SSOProvider = z
    .object({
    entityType: z.string().optional(),
    id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
    ssoProviderId: z
        .string()
        .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
        .optional(),
    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    providerType: z.enum(["saml", "oidc", "oauth2"]),
    providerId: z.string(),
    enabled: z.boolean().default(true),
    name: z.string().optional(),
    description: z.string().optional(),
    samlMetadataUrl: z.string().url().optional(),
    samlMetadataXml: z.string().optional(),
    samlEntityId: z.string().optional(),
    samlAcsUrl: z.string().url().optional(),
    samlSigningCertificate: z.string().optional(),
    oidcDiscoveryUrl: z.string().url().optional(),
    oidcClientId: z.string().optional(),
    oidcClientSecret: z.string().optional(),
    oidcScopes: z.array(z.string()).optional(),
    attributeMapping: z
        .object({
        email: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        displayName: z.string(),
        roles: z.string(),
        groups: z.string(),
    })
        .partial()
        .passthrough()
        .optional(),
    jitProvisioning: z.boolean().optional().default(false),
    enforceSSO: z.boolean().optional().default(false),
    defaultRoles: z.array(z.string()).optional(),
    configuration: z
        .object({
        issuerUrl: z.string().url(),
        clientId: z.string(),
        scopes: z.array(z.string()),
        attributeMapping: z
            .object({
            email: z.string(),
            name: z.string(),
            firstName: z.string(),
            lastName: z.string(),
        })
            .partial()
            .passthrough(),
    })
        .partial()
        .passthrough()
        .optional(),
    domainRestriction: z.array(z.string()).optional(),
    createdBy: z.union([z.string(), z.null()]).optional(),
    metadata: z.object({}).partial().passthrough().optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const SSOProviderResponse = z
    .object({
    data: z
        .object({
        entityType: z.string().optional(),
        id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoProviderId: z
            .string()
            .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
            .optional(),
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        providerType: z.enum(["saml", "oidc", "oauth2"]),
        providerId: z.string(),
        enabled: z.boolean().default(true),
        name: z.string().optional(),
        description: z.string().optional(),
        samlMetadataUrl: z.string().url().optional(),
        samlMetadataXml: z.string().optional(),
        samlEntityId: z.string().optional(),
        samlAcsUrl: z.string().url().optional(),
        samlSigningCertificate: z.string().optional(),
        oidcDiscoveryUrl: z.string().url().optional(),
        oidcClientId: z.string().optional(),
        oidcClientSecret: z.string().optional(),
        oidcScopes: z.array(z.string()).optional(),
        attributeMapping: z
            .object({
            email: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            displayName: z.string(),
            roles: z.string(),
            groups: z.string(),
        })
            .partial()
            .passthrough()
            .optional(),
        jitProvisioning: z.boolean().optional().default(false),
        enforceSSO: z.boolean().optional().default(false),
        defaultRoles: z.array(z.string()).optional(),
        configuration: z
            .object({
            issuerUrl: z.string().url(),
            clientId: z.string(),
            scopes: z.array(z.string()),
            attributeMapping: z
                .object({
                email: z.string(),
                name: z.string(),
                firstName: z.string(),
                lastName: z.string(),
            })
                .partial()
                .passthrough(),
        })
            .partial()
            .passthrough()
            .optional(),
        domainRestriction: z.array(z.string()).optional(),
        createdBy: z.union([z.string(), z.null()]).optional(),
        metadata: z.object({}).partial().passthrough().optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const SSOProviderIdPathParam = z.string();
const SSOProviderTestResponse = z
    .object({
    data: z
        .object({
        success: z.boolean(),
        errors: z
            .array(z
            .object({ code: z.string(), message: z.string() })
            .partial()
            .passthrough())
            .optional(),
        warnings: z
            .array(z
            .object({ code: z.string(), message: z.string() })
            .partial()
            .passthrough())
            .optional(),
        metadataValid: z.boolean().optional(),
        connectionValid: z.boolean().optional(),
        certificateValid: z.boolean().optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
const SSOSettings = z
    .object({
    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
    ssoEnabled: z.boolean().default(false),
    enforceSSO: z.boolean().optional().default(false),
    allowedProviders: z
        .array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/))
        .optional(),
    defaultProvider: z.union([z.string(), z.null()]).optional(),
    jitProvisioning: z.boolean().optional().default(false),
    jitProvisioningDomainWhitelist: z.array(z.string()).optional(),
    jitProvisioningDefaultRoles: z.array(z.string()).optional(),
    createdAt: z.string().datetime({ offset: true }).optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
})
    .passthrough();
const SSOSettingsResponse = z
    .object({
    data: z
        .object({
        orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
        ssoEnabled: z.boolean().default(false),
        enforceSSO: z.boolean().optional().default(false),
        allowedProviders: z
            .array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/))
            .optional(),
        defaultProvider: z.union([z.string(), z.null()]).optional(),
        jitProvisioning: z.boolean().optional().default(false),
        jitProvisioningDomainWhitelist: z.array(z.string()).optional(),
        jitProvisioningDefaultRoles: z.array(z.string()).optional(),
        createdAt: z.string().datetime({ offset: true }).optional(),
        updatedAt: z.string().datetime({ offset: true }).optional(),
    })
        .passthrough(),
    meta: z
        .object({
        correlationId: z.string().regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
        timestamp: z.string().datetime({ offset: true }),
        requestId: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough();
export const schemas = {
    CreateOrgIdentityOrg_Body,
    PatchOrgIdentityOrg_Body,
    CreateOrgIdentitySession_Body,
    RefreshOrgIdentitySession_Body,
    PatchOrgIdentityProfile_Body,
    CreateOrgIdentityUser_Body,
    PatchOrgIdentityUser_Body,
    CompleteOrgIdentityUserOnboardingStep_Body,
    CreateOrgIdentityRole_Body,
    UpdateOrgIdentityRole_Body,
    CreateOrgIdentityApiKey_Body,
    CreateOrgIdentitySSOProvider_Body,
    UpdateOrgIdentitySSOProvider_Body,
    PatchOrgIdentitySSOProvider_Body,
    UpdateOrgIdentitySSOSettings_Body,
    PostOrgsRequest,
    PostOrgsResponse,
    ListOrgsResponse,
    GetOrgByIdResponse,
    UpdateOrgRequest,
    UpdateOrgByIdResponse,
    PostAuthSessionsRequest,
    PostAuthSessionsResponse,
    RefreshAuthSessionRequest,
    RefreshAuthSessionResponse,
    PasswordResetRequest,
    PasswordResetResponse,
    UserPasswordResetRequest,
    UserPasswordResetResponse,
    ListSessionsResponse,
    GetSessionResponse,
    CompleteUserOnboardingStepRequest,
    CompleteUserOnboardingStepResponse,
    CreateSSOProviderRequest,
    CreateSSOProviderResponse,
    GetSSOProviderResponse,
    GetSSOSettingsResponse,
    GetUserOnboardingResponse,
    ListOrgIdentityOnboardingStatusesResponse,
    OrgIdentityOnboardingStatus,
    UserOnboarding,
    GetUserResponse,
    ListRolesResponse,
    CreateRoleRequest,
    CreateRoleResponse,
    GetRoleResponse,
    UpdateRoleRequest,
    UpdateRoleResponse,
    CreateApiKeyRequest,
    CreateApiKeyResponse,
    GetApiKeyResponse,
    ListApiKeysResponse,
    RevokeApiKeyResponse,
    ApiKey,
    ApiKeyPublic,
    ApiKeyScope,
    ListSSOProvidersResponse,
    ListUsersResponse,
    PostUsersRequest,
    PostUsersResponse,
    Problem,
    TestSSOProviderResponse,
    UpdateOrgSettingsRequest,
    PatchSSOProviderRequest,
    UpdateSSOProviderRequest,
    UpdateSSOProviderResponse,
    UpdateSSOSettingsRequest,
    UpdateSSOSettingsResponse,
    UpdateUserRequest,
    UpdateUserResponse,
    IdentityProfile,
    UpdateIdentityProfileRequest,
    GetIdentityProfileResponse,
    GetCurrentIdentityResponse,
    UploadIdentityProfileMediaRequest,
    ResponseMeta,
    CursorQueryParam,
    LimitQueryParamHigh,
    OrgId,
    OrgSettings,
    Org,
    PageInfo,
    CreateOrgRequest,
    OrgIdPathParam,
    LoginRequest,
    LoginPayload,
    LoginResponse,
    RefreshRequest,
    RefreshPayload,
    Role,
    User,
    CreateUserRequest,
    UserIdPathParam,
    CompleteOnboardingStepRequest,
    GetOnboardingStatusResponse,
    OnboardingStep,
    SessionEntity,
    RoleEntity,
    CreateApiKeyData,
    SSOProvider,
    SSOProviderResponse,
    SSOProviderIdPathParam,
    SSOProviderTestResponse,
    SSOSettings,
    SSOSettingsResponse,
};
const endpoints = makeApi([
    {
        method: "get",
        path: "/identity/current",
        alias: "GetIdentityCurrent",
        description: `Returns &#x60;{ orgId, scopes, apiKeyId, userId, authType }&#x60; for the caller&#x27;s API key or JWT. Unscoped (no org path); used by hosted MCP.
`,
        requestFormat: "json",
        response: z
            .object({
            data: z
                .object({
                orgId: z.string(),
                scopes: z.array(z.string()).optional(),
                apiKeyId: z.string().optional(),
                userId: z.string().optional(),
                authType: z.string().optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/identity/orgs",
        alias: "CreateOrgIdentityOrg",
        description: `Creates a new organization. Typically used during signup or when the current user creates an additional org. Returns the created org (identity idt_ prefix).`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CreateOrgIdentityOrg_Body,
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    entityType: z.string().optional(),
                    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    orgId: z
                        .string()
                        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                        .optional(),
                    name: z.string(),
                    slug: z.string().optional(),
                    status: z
                        .enum(["active", "suspended", "disabled"])
                        .optional(),
                    domain: z.string().optional(),
                    settings: z
                        .object({
                        allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
                        requireMfa: z.boolean().default(false),
                        sessionTimeoutMinutes: z.number().int(),
                        maxSessionsPerUser: z.number().int(),
                        timezone: z.string(),
                        locale: z.string(),
                        defaultCurrency: z.string(),
                        featureFlags: z.record(z.boolean()),
                        enabledSocialChannelSlugs: z.array(z.string()),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    metadata: z.object({}).partial().passthrough().optional(),
                    createdAt: z.string().datetime({ offset: true }).optional(),
                    updatedAt: z.string().datetime({ offset: true }).optional(),
                })
                    .passthrough()),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough()
                .and(z
                .object({
                pagination: z
                    .object({
                    nextCursor: z.union([z.string(), z.null()]).optional(),
                    prevCursor: z.union([z.string(), z.null()]).optional(),
                    limit: z.number().int().gte(1).lte(250).default(50),
                })
                    .passthrough(),
            })
                .passthrough()),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/identity/orgs",
        alias: "ListOrgIdentityOrgs",
        description: `Returns organizations the current user belongs to. Use for org switcher or &quot;my orgs&quot; list.`,
        requestFormat: "json",
        parameters: [
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    entityType: z.string().optional(),
                    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    orgId: z
                        .string()
                        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                        .optional(),
                    name: z.string(),
                    slug: z.string().optional(),
                    status: z
                        .enum(["active", "suspended", "disabled"])
                        .optional(),
                    domain: z.string().optional(),
                    settings: z
                        .object({
                        allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
                        requireMfa: z.boolean().default(false),
                        sessionTimeoutMinutes: z.number().int(),
                        maxSessionsPerUser: z.number().int(),
                        timezone: z.string(),
                        locale: z.string(),
                        defaultCurrency: z.string(),
                        featureFlags: z.record(z.boolean()),
                        enabledSocialChannelSlugs: z.array(z.string()),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    metadata: z.object({}).partial().passthrough().optional(),
                    createdAt: z.string().datetime({ offset: true }).optional(),
                    updatedAt: z.string().datetime({ offset: true }).optional(),
                })
                    .passthrough()),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough()
                .and(z
                .object({
                pagination: z
                    .object({
                    nextCursor: z.union([z.string(), z.null()]).optional(),
                    prevCursor: z.union([z.string(), z.null()]).optional(),
                    limit: z.number().int().gte(1).lte(250).default(50),
                })
                    .passthrough(),
            })
                .passthrough()),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/api-keys",
        alias: "ListOrgIdentityApiKeys",
        description: `Returns metadata for org API keys (prefix, scopes, status). Never returns plaintext or keyHash.`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    entityType: z.string().optional(),
                    id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
                    keyPrefix: z.string(),
                    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    userId: z.string().optional(),
                    name: z.string().optional(),
                    status: z.enum(["active", "revoked", "expired"]),
                    scopes: z
                        .array(z.enum([
                        "mcp:invoke",
                        "mcp:admin",
                        "content:read",
                        "content:write",
                        "publishing:read",
                        "publishing:write",
                        "publishing:dispatch",
                        "social:read",
                        "social:write",
                        "social:reply",
                        "connectivity:read",
                        "connectivity:write",
                        "focus:read",
                        "focus:write",
                        "analytics:read",
                        "knowledge:read",
                        "knowledge:write",
                    ]))
                        .optional(),
                    lastUsedAt: z.string().datetime({ offset: true }).optional(),
                    expiresAt: z.union([z.string(), z.null()]).optional(),
                    createdBy: z.string().optional(),
                    metadata: z.object({}).partial().passthrough().optional(),
                    createdAt: z.string().datetime({ offset: true }).optional(),
                    updatedAt: z.string().datetime({ offset: true }).optional(),
                })
                    .passthrough()),
                nextToken: z.string().optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/api-keys",
        alias: "CreateOrgIdentityApiKey",
        description: `Creates an org-scoped API key. Requires JWT (org admin). Returns plaintext secret once.
Default scopes are MCP read scopes when body.scopes is omitted. Writes are opt-in.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CreateOrgIdentityApiKey_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
                keyPrefix: z.string(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                userId: z.string().optional(),
                name: z.string().optional(),
                status: z.enum(["active", "revoked", "expired"]),
                scopes: z
                    .array(z.enum([
                    "mcp:invoke",
                    "mcp:admin",
                    "content:read",
                    "content:write",
                    "publishing:read",
                    "publishing:write",
                    "publishing:dispatch",
                    "social:read",
                    "social:write",
                    "social:reply",
                    "connectivity:read",
                    "connectivity:write",
                    "focus:read",
                    "focus:write",
                    "analytics:read",
                    "knowledge:read",
                    "knowledge:write",
                ]))
                    .optional(),
                lastUsedAt: z.string().datetime({ offset: true }).optional(),
                expiresAt: z.union([z.string(), z.null()]).optional(),
                createdBy: z.string().optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough()
                .and(z.object({ secret: z.string() }).passthrough()),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/api-keys/:apiKeyId",
        alias: "GetOrgIdentityApiKey",
        description: `Returns API key metadata. Never returns plaintext or keyHash.`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "apiKeyId",
                type: "Path",
                schema: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
                keyPrefix: z.string(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                userId: z.string().optional(),
                name: z.string().optional(),
                status: z.enum(["active", "revoked", "expired"]),
                scopes: z
                    .array(z.enum([
                    "mcp:invoke",
                    "mcp:admin",
                    "content:read",
                    "content:write",
                    "publishing:read",
                    "publishing:write",
                    "publishing:dispatch",
                    "social:read",
                    "social:write",
                    "social:reply",
                    "connectivity:read",
                    "connectivity:write",
                    "focus:read",
                    "focus:write",
                    "analytics:read",
                    "knowledge:read",
                    "knowledge:write",
                ]))
                    .optional(),
                lastUsedAt: z.string().datetime({ offset: true }).optional(),
                expiresAt: z.union([z.string(), z.null()]).optional(),
                createdBy: z.string().optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/api-keys/:apiKeyId/revoke",
        alias: "RevokeOrgIdentityApiKey",
        description: `Sets key status to revoked. Requires JWT (org admin).`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "apiKeyId",
                type: "Path",
                schema: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^apk_[0-9a-hjkmnp-tv-z]{26}$/),
                keyPrefix: z.string(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                userId: z.string().optional(),
                name: z.string().optional(),
                status: z.enum(["active", "revoked", "expired"]),
                scopes: z
                    .array(z.enum([
                    "mcp:invoke",
                    "mcp:admin",
                    "content:read",
                    "content:write",
                    "publishing:read",
                    "publishing:write",
                    "publishing:dispatch",
                    "social:read",
                    "social:write",
                    "social:reply",
                    "connectivity:read",
                    "connectivity:write",
                    "focus:read",
                    "focus:write",
                    "analytics:read",
                    "knowledge:read",
                    "knowledge:write",
                ]))
                    .optional(),
                lastUsedAt: z.string().datetime({ offset: true }).optional(),
                expiresAt: z.union([z.string(), z.null()]).optional(),
                createdBy: z.string().optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/auth/password/reset",
        alias: "RequestOrgIdentityPasswordReset",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: z.object({ email: z.string().email() }).passthrough(),
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.object({ message: z.string() }).partial().passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/auth/sessions",
        alias: "CreateOrgIdentitySession",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CreateOrgIdentitySession_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                accessToken: z.string(),
                refreshToken: z.string().optional(),
                orgIds: z.array(z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)),
                userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                role: z.string(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/auth/sessions",
        alias: "LogoutOrgIdentitySession",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/auth/sessions/refresh",
        alias: "RefreshOrgIdentitySession",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: RefreshOrgIdentitySession_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({ accessToken: z.string(), refreshToken: z.string() })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/onboarding-statuses",
        alias: "ListOrgIdentityOnboardingStatuses",
        description: `Lists onboarding progress for users in the organization. Returns per-user step completion, current step, and completion percentage for admin tracking.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    userName: z.string().optional(),
                    userEmail: z.string().optional(),
                    completed: z.boolean(),
                    steps: z.array(z
                        .object({
                        id: z.string(),
                        name: z.string(),
                        completed: z.boolean(),
                        completedAt: z
                            .string()
                            .datetime({ offset: true })
                            .optional(),
                    })
                        .passthrough()),
                    currentStep: z.string().optional(),
                    completionPercentage: z.number().gte(0).lte(100),
                    onboardingStartedAt: z
                        .string()
                        .datetime({ offset: true })
                        .optional(),
                    onboardingCompletedAt: z
                        .string()
                        .datetime({ offset: true })
                        .optional(),
                    lastActivityAt: z
                        .string()
                        .datetime({ offset: true })
                        .optional(),
                    userCreatedAt: z
                        .string()
                        .datetime({ offset: true })
                        .optional(),
                })
                    .passthrough()),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough()
                .and(z
                .object({
                pagination: z
                    .object({
                    nextCursor: z.union([z.string(), z.null()]).optional(),
                    prevCursor: z.union([z.string(), z.null()]).optional(),
                    limit: z.number().int().gte(1).lte(250).default(50),
                })
                    .passthrough(),
            })
                .passthrough()),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/org",
        alias: "GetOrgIdentityOrg",
        description: `Returns the organization for the given orgId. Caller must be a member of the org. Use as &quot;current org&quot; or &quot;me&quot; in org context.`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                status: z.enum(["active", "suspended", "disabled"]).optional(),
                domain: z.string().optional(),
                settings: z
                    .object({
                    allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
                    requireMfa: z.boolean().default(false),
                    sessionTimeoutMinutes: z.number().int(),
                    maxSessionsPerUser: z.number().int(),
                    timezone: z.string(),
                    locale: z.string(),
                    defaultCurrency: z.string(),
                    featureFlags: z.record(z.boolean()),
                    enabledSocialChannelSlugs: z.array(z.string()),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "patch",
        path: "/orgs/:orgId/identity/org",
        alias: "PatchOrgIdentityOrg",
        description: `Patches the organization. Caller must have org admin (or equivalent) permission.`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: PatchOrgIdentityOrg_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                status: z.enum(["active", "suspended", "disabled"]).optional(),
                domain: z.string().optional(),
                settings: z
                    .object({
                    allowedAuthMethods: z.array(z.enum(["password", "sso", "magic_link"])),
                    requireMfa: z.boolean().default(false),
                    sessionTimeoutMinutes: z.number().int(),
                    maxSessionsPerUser: z.number().int(),
                    timezone: z.string(),
                    locale: z.string(),
                    defaultCurrency: z.string(),
                    featureFlags: z.record(z.boolean()),
                    enabledSocialChannelSlugs: z.array(z.string()),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/org",
        alias: "DeleteOrgIdentityOrg",
        description: `Deletes the organization. Caller must have org admin permission. May require confirmation or soft-delete semantics per implementation.`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/profile",
        alias: "GetOrgIdentityProfile",
        description: `Returns the authenticated user&#x27;s profile for the org. Avatar and cover
URLs are freshly presigned; durable S3 keys are never returned.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                displayName: z.string().optional(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                avatarUrl: z.union([z.string(), z.null()]).optional(),
                coverUrl: z.union([z.string(), z.null()]).optional(),
                profile: z
                    .object({ timezone: z.string(), locale: z.string() })
                    .partial()
                    .passthrough()
                    .optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "patch",
        path: "/orgs/:orgId/identity/profile",
        alias: "PatchOrgIdentityProfile",
        description: `Updates safe self-service fields for the authenticated user only
(displayName, firstName, lastName, timezone, locale). Roles, status,
and email cannot be changed through this endpoint.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: PatchOrgIdentityProfile_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                displayName: z.string().optional(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                avatarUrl: z.union([z.string(), z.null()]).optional(),
                coverUrl: z.union([z.string(), z.null()]).optional(),
                profile: z
                    .object({ timezone: z.string(), locale: z.string() })
                    .partial()
                    .passthrough()
                    .optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/profile/media/:kind",
        alias: "UploadOrgIdentityProfileMedia",
        description: `Uploads an image for the authenticated user&#x27;s avatar or cover photo.
Stores the object under an identity-scoped S3 key and returns a freshly
presigned read URL.
`,
        requestFormat: "form-data",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: z.object({ file: z.instanceof(File) }).passthrough(),
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "kind",
                type: "Path",
                schema: z.enum(["avatar", "cover"]),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                displayName: z.string().optional(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                avatarUrl: z.union([z.string(), z.null()]).optional(),
                coverUrl: z.union([z.string(), z.null()]).optional(),
                profile: z
                    .object({ timezone: z.string(), locale: z.string() })
                    .partial()
                    .passthrough()
                    .optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/profile/media/:kind",
        alias: "DeleteOrgIdentityProfileMedia",
        description: `Clears the authenticated user&#x27;s avatar or cover reference and deletes
the stored S3 object.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "kind",
                type: "Path",
                schema: z.enum(["avatar", "cover"]),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                userId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                displayName: z.string().optional(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                avatarUrl: z.union([z.string(), z.null()]).optional(),
                coverUrl: z.union([z.string(), z.null()]).optional(),
                profile: z
                    .object({ timezone: z.string(), locale: z.string() })
                    .partial()
                    .passthrough()
                    .optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/roles",
        alias: "ListOrgIdentityRoles",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            items: z.array(z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                description: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                isSystem: z.boolean().optional().default(false),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough()),
            nextToken: z.string().optional(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/roles",
        alias: "CreateOrgIdentityRole",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CreateOrgIdentityRole_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                description: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                isSystem: z.boolean().optional().default(false),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/roles/:roleId",
        alias: "GetOrgIdentityRole",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "roleId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                description: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                isSystem: z.boolean().optional().default(false),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "put",
        path: "/orgs/:orgId/identity/roles/:roleId",
        alias: "UpdateOrgIdentityRole",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: UpdateOrgIdentityRole_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "roleId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                description: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                isSystem: z.boolean().optional().default(false),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "patch",
        path: "/orgs/:orgId/identity/roles/:roleId",
        alias: "PatchOrgIdentityRole",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: UpdateOrgIdentityRole_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "roleId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                name: z.string(),
                slug: z.string().optional(),
                description: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                isSystem: z.boolean().optional().default(false),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/roles/:roleId",
        alias: "DeleteOrgIdentityRole",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "roleId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/sessions",
        alias: "ListOrgIdentitySessions",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            items: z.array(z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z.string().optional(),
                userId: z.string().optional(),
                status: z.enum(["active", "expired", "revoked"]).optional(),
                ipAddress: z.string().optional(),
                userAgent: z.string().optional(),
                deviceType: z.string().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                lastActivityAt: z.string().datetime({ offset: true }).optional(),
                expiresAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough()),
            nextToken: z.string().optional(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/sessions/:sessionId",
        alias: "GetOrgIdentitySession",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "sessionId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                orgId: z.string().optional(),
                userId: z.string().optional(),
                status: z.enum(["active", "expired", "revoked"]).optional(),
                ipAddress: z.string().optional(),
                userAgent: z.string().optional(),
                deviceType: z.string().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                lastActivityAt: z.string().datetime({ offset: true }).optional(),
                expiresAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/sessions/:sessionId",
        alias: "DeleteOrgIdentitySession",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "sessionId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/sso-providers",
        alias: "ListOrgIdentitySSOProviders",
        description: `Lists all SSO providers configured for the organization. Returns providers
for SAML 2.0, OIDC, and OAuth 2.0 authentication.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    entityType: z.string().optional(),
                    id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
                    ssoProviderId: z
                        .string()
                        .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
                        .optional(),
                    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    providerType: z.enum(["saml", "oidc", "oauth2"]),
                    providerId: z.string(),
                    enabled: z.boolean().default(true),
                    name: z.string().optional(),
                    description: z.string().optional(),
                    samlMetadataUrl: z.string().url().optional(),
                    samlMetadataXml: z.string().optional(),
                    samlEntityId: z.string().optional(),
                    samlAcsUrl: z.string().url().optional(),
                    samlSigningCertificate: z.string().optional(),
                    oidcDiscoveryUrl: z.string().url().optional(),
                    oidcClientId: z.string().optional(),
                    oidcClientSecret: z.string().optional(),
                    oidcScopes: z.array(z.string()).optional(),
                    attributeMapping: z
                        .object({
                        email: z.string(),
                        firstName: z.string(),
                        lastName: z.string(),
                        displayName: z.string(),
                        roles: z.string(),
                        groups: z.string(),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    jitProvisioning: z.boolean().optional().default(false),
                    enforceSSO: z.boolean().optional().default(false),
                    defaultRoles: z.array(z.string()).optional(),
                    configuration: z
                        .object({
                        issuerUrl: z.string().url(),
                        clientId: z.string(),
                        scopes: z.array(z.string()),
                        attributeMapping: z
                            .object({
                            email: z.string(),
                            name: z.string(),
                            firstName: z.string(),
                            lastName: z.string(),
                        })
                            .partial()
                            .passthrough(),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    domainRestriction: z.array(z.string()).optional(),
                    createdBy: z.union([z.string(), z.null()]).optional(),
                    metadata: z.object({}).partial().passthrough().optional(),
                    createdAt: z.string().datetime({ offset: true }).optional(),
                    updatedAt: z.string().datetime({ offset: true }).optional(),
                })
                    .passthrough()),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough()
                .and(z
                .object({
                pagination: z
                    .object({
                    nextCursor: z.union([z.string(), z.null()]).optional(),
                    prevCursor: z.union([z.string(), z.null()]).optional(),
                    limit: z.number().int().gte(1).lte(250).default(50),
                })
                    .passthrough(),
            })
                .passthrough()),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/sso-providers",
        alias: "CreateOrgIdentitySSOProvider",
        description: `Creates a new SSO provider configuration for the organization. Supports
SAML 2.0, OIDC, and OAuth 2.0 providers. The provider can be configured
with metadata URLs, certificates, client credentials, and attribute mappings.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CreateOrgIdentitySSOProvider_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
                ssoProviderId: z
                    .string()
                    .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                providerType: z.enum(["saml", "oidc", "oauth2"]),
                providerId: z.string(),
                enabled: z.boolean().default(true),
                name: z.string().optional(),
                description: z.string().optional(),
                samlMetadataUrl: z.string().url().optional(),
                samlMetadataXml: z.string().optional(),
                samlEntityId: z.string().optional(),
                samlAcsUrl: z.string().url().optional(),
                samlSigningCertificate: z.string().optional(),
                oidcDiscoveryUrl: z.string().url().optional(),
                oidcClientId: z.string().optional(),
                oidcClientSecret: z.string().optional(),
                oidcScopes: z.array(z.string()).optional(),
                attributeMapping: z
                    .object({
                    email: z.string(),
                    firstName: z.string(),
                    lastName: z.string(),
                    displayName: z.string(),
                    roles: z.string(),
                    groups: z.string(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                jitProvisioning: z.boolean().optional().default(false),
                enforceSSO: z.boolean().optional().default(false),
                defaultRoles: z.array(z.string()).optional(),
                configuration: z
                    .object({
                    issuerUrl: z.string().url(),
                    clientId: z.string(),
                    scopes: z.array(z.string()),
                    attributeMapping: z
                        .object({
                        email: z.string(),
                        name: z.string(),
                        firstName: z.string(),
                        lastName: z.string(),
                    })
                        .partial()
                        .passthrough(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                domainRestriction: z.array(z.string()).optional(),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/sso-providers/:providerId",
        alias: "GetOrgIdentitySSOProvider",
        description: `Retrieves the configuration for a specific SSO provider. Returns provider
details including metadata, attribute mappings, and settings.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "providerId",
                type: "Path",
                schema: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
                ssoProviderId: z
                    .string()
                    .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                providerType: z.enum(["saml", "oidc", "oauth2"]),
                providerId: z.string(),
                enabled: z.boolean().default(true),
                name: z.string().optional(),
                description: z.string().optional(),
                samlMetadataUrl: z.string().url().optional(),
                samlMetadataXml: z.string().optional(),
                samlEntityId: z.string().optional(),
                samlAcsUrl: z.string().url().optional(),
                samlSigningCertificate: z.string().optional(),
                oidcDiscoveryUrl: z.string().url().optional(),
                oidcClientId: z.string().optional(),
                oidcClientSecret: z.string().optional(),
                oidcScopes: z.array(z.string()).optional(),
                attributeMapping: z
                    .object({
                    email: z.string(),
                    firstName: z.string(),
                    lastName: z.string(),
                    displayName: z.string(),
                    roles: z.string(),
                    groups: z.string(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                jitProvisioning: z.boolean().optional().default(false),
                enforceSSO: z.boolean().optional().default(false),
                defaultRoles: z.array(z.string()).optional(),
                configuration: z
                    .object({
                    issuerUrl: z.string().url(),
                    clientId: z.string(),
                    scopes: z.array(z.string()),
                    attributeMapping: z
                        .object({
                        email: z.string(),
                        name: z.string(),
                        firstName: z.string(),
                        lastName: z.string(),
                    })
                        .partial()
                        .passthrough(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                domainRestriction: z.array(z.string()).optional(),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "put",
        path: "/orgs/:orgId/identity/sso-providers/:providerId",
        alias: "UpdateOrgIdentitySSOProvider",
        description: `Updates the configuration for an SSO provider. Can update metadata,
credentials, attribute mappings, and provider settings.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: UpdateOrgIdentitySSOProvider_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "providerId",
                type: "Path",
                schema: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
                ssoProviderId: z
                    .string()
                    .regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                providerType: z.enum(["saml", "oidc", "oauth2"]),
                providerId: z.string(),
                enabled: z.boolean().default(true),
                name: z.string().optional(),
                description: z.string().optional(),
                samlMetadataUrl: z.string().url().optional(),
                samlMetadataXml: z.string().optional(),
                samlEntityId: z.string().optional(),
                samlAcsUrl: z.string().url().optional(),
                samlSigningCertificate: z.string().optional(),
                oidcDiscoveryUrl: z.string().url().optional(),
                oidcClientId: z.string().optional(),
                oidcClientSecret: z.string().optional(),
                oidcScopes: z.array(z.string()).optional(),
                attributeMapping: z
                    .object({
                    email: z.string(),
                    firstName: z.string(),
                    lastName: z.string(),
                    displayName: z.string(),
                    roles: z.string(),
                    groups: z.string(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                jitProvisioning: z.boolean().optional().default(false),
                enforceSSO: z.boolean().optional().default(false),
                defaultRoles: z.array(z.string()).optional(),
                configuration: z
                    .object({
                    issuerUrl: z.string().url(),
                    clientId: z.string(),
                    scopes: z.array(z.string()),
                    attributeMapping: z
                        .object({
                        email: z.string(),
                        name: z.string(),
                        firstName: z.string(),
                        lastName: z.string(),
                    })
                        .partial()
                        .passthrough(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                domainRestriction: z.array(z.string()).optional(),
                createdBy: z.union([z.string(), z.null()]).optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "patch",
        path: "/orgs/:orgId/identity/sso-providers/:providerId",
        alias: "PatchOrgIdentitySSOProvider",
        description: `Performs an action on the SSO provider. Action test validates connection, metadata, and credentials.`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: PatchOrgIdentitySSOProvider_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "providerId",
                type: "Path",
                schema: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                success: z.boolean(),
                errors: z
                    .array(z
                    .object({ code: z.string(), message: z.string() })
                    .partial()
                    .passthrough())
                    .optional(),
                warnings: z
                    .array(z
                    .object({ code: z.string(), message: z.string() })
                    .partial()
                    .passthrough())
                    .optional(),
                metadataValid: z.boolean().optional(),
                connectionValid: z.boolean().optional(),
                certificateValid: z.boolean().optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/sso-providers/:providerId",
        alias: "DeleteOrgIdentitySSOProvider",
        description: `Removes an SSO provider configuration from the organization. This will
disable SSO authentication for that provider but will not affect existing
user accounts.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "providerId",
                type: "Path",
                schema: z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/sso-settings",
        alias: "GetOrgIdentitySSOSettings",
        description: `Retrieves the organization&#x27;s SSO settings including enforcement policies,
default provider, and JIT provisioning configuration.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                ssoEnabled: z.boolean().default(false),
                enforceSSO: z.boolean().optional().default(false),
                allowedProviders: z
                    .array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/))
                    .optional(),
                defaultProvider: z.union([z.string(), z.null()]).optional(),
                jitProvisioning: z.boolean().optional().default(false),
                jitProvisioningDomainWhitelist: z.array(z.string()).optional(),
                jitProvisioningDefaultRoles: z.array(z.string()).optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "put",
        path: "/orgs/:orgId/identity/sso-settings",
        alias: "UpdateOrgIdentitySSOSettings",
        description: `Updates the organization&#x27;s SSO settings. Can enable/disable SSO enforcement,
configure default provider, and manage JIT provisioning settings.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: UpdateOrgIdentitySSOSettings_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                ssoEnabled: z.boolean().default(false),
                enforceSSO: z.boolean().optional().default(false),
                allowedProviders: z
                    .array(z.string().regex(/^sso_[0-9a-hjkmnp-tv-z]{26}$/))
                    .optional(),
                defaultProvider: z.union([z.string(), z.null()]).optional(),
                jitProvisioning: z.boolean().optional().default(false),
                jitProvisioningDomainWhitelist: z.array(z.string()).optional(),
                jitProvisioningDefaultRoles: z.array(z.string()).optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/users",
        alias: "ListOrgIdentityUsers",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "cursor",
                type: "Query",
                schema: z.string().optional(),
            },
            {
                name: "limit",
                type: "Query",
                schema: z.number().int().gte(1).lte(250).optional().default(50),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    entityType: z.string().optional(),
                    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    userId: z
                        .string()
                        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                        .optional(),
                    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    email: z.string().email(),
                    emailVerified: z.boolean().optional(),
                    displayName: z.string(),
                    firstName: z.string().optional(),
                    lastName: z.string().optional(),
                    avatar: z.union([z.string(), z.null()]).optional(),
                    avatarS3Key: z.union([z.string(), z.null()]).optional(),
                    coverS3Key: z.union([z.string(), z.null()]).optional(),
                    phone: z.union([z.string(), z.null()]).optional(),
                    phoneVerified: z.boolean().optional().default(false),
                    roles: z
                        .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
                        .optional(),
                    role: z.string().optional(),
                    permissions: z.array(z.string()).optional(),
                    status: z.enum([
                        "active",
                        "suspended",
                        "invited",
                        "deactivated",
                        "pending_verification",
                        "disabled",
                    ]),
                    profile: z
                        .object({
                        department: z.string(),
                        title: z.string(),
                        timezone: z.string(),
                        locale: z.string(),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    authentication: z
                        .object({
                        lastLoginAt: z.union([z.string(), z.null()]),
                        mfaEnrolled: z.boolean(),
                        mfaMethod: z.union([z.string(), z.null()]),
                        passwordChangedAt: z.union([z.string(), z.null()]),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    metadata: z.object({}).partial().passthrough().optional(),
                    tags: z.array(z.string()).optional(),
                    createdBy: z.union([z.string(), z.null()]).optional(),
                    updatedBy: z.union([z.string(), z.null()]).optional(),
                    createdAt: z.string().datetime({ offset: true }).optional(),
                    updatedAt: z.string().datetime({ offset: true }).optional(),
                })
                    .passthrough()),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough()
                .and(z
                .object({
                pagination: z
                    .object({
                    nextCursor: z.union([z.string(), z.null()]).optional(),
                    prevCursor: z.union([z.string(), z.null()]).optional(),
                    limit: z.number().int().gte(1).lte(250).default(50),
                })
                    .passthrough(),
            })
                .passthrough()),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/users",
        alias: "CreateOrgIdentityUser",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CreateOrgIdentityUser_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                items: z.array(z
                    .object({
                    entityType: z.string().optional(),
                    id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    userId: z
                        .string()
                        .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                        .optional(),
                    orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                    email: z.string().email(),
                    emailVerified: z.boolean().optional(),
                    displayName: z.string(),
                    firstName: z.string().optional(),
                    lastName: z.string().optional(),
                    avatar: z.union([z.string(), z.null()]).optional(),
                    avatarS3Key: z.union([z.string(), z.null()]).optional(),
                    coverS3Key: z.union([z.string(), z.null()]).optional(),
                    phone: z.union([z.string(), z.null()]).optional(),
                    phoneVerified: z.boolean().optional().default(false),
                    roles: z
                        .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
                        .optional(),
                    role: z.string().optional(),
                    permissions: z.array(z.string()).optional(),
                    status: z.enum([
                        "active",
                        "suspended",
                        "invited",
                        "deactivated",
                        "pending_verification",
                        "disabled",
                    ]),
                    profile: z
                        .object({
                        department: z.string(),
                        title: z.string(),
                        timezone: z.string(),
                        locale: z.string(),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    authentication: z
                        .object({
                        lastLoginAt: z.union([z.string(), z.null()]),
                        mfaEnrolled: z.boolean(),
                        mfaMethod: z.union([z.string(), z.null()]),
                        passwordChangedAt: z.union([z.string(), z.null()]),
                    })
                        .partial()
                        .passthrough()
                        .optional(),
                    metadata: z.object({}).partial().passthrough().optional(),
                    tags: z.array(z.string()).optional(),
                    createdBy: z.union([z.string(), z.null()]).optional(),
                    updatedBy: z.union([z.string(), z.null()]).optional(),
                    createdAt: z.string().datetime({ offset: true }).optional(),
                    updatedAt: z.string().datetime({ offset: true }).optional(),
                })
                    .passthrough()),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough()
                .and(z
                .object({
                pagination: z
                    .object({
                    nextCursor: z.union([z.string(), z.null()]).optional(),
                    prevCursor: z.union([z.string(), z.null()]).optional(),
                    limit: z.number().int().gte(1).lte(250).default(50),
                })
                    .passthrough(),
            })
                .passthrough()),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/users/:userId",
        alias: "GetOrgIdentityUser",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "userId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                userId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                emailVerified: z.boolean().optional(),
                displayName: z.string(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                avatar: z.union([z.string(), z.null()]).optional(),
                avatarS3Key: z.union([z.string(), z.null()]).optional(),
                coverS3Key: z.union([z.string(), z.null()]).optional(),
                phone: z.union([z.string(), z.null()]).optional(),
                phoneVerified: z.boolean().optional().default(false),
                roles: z
                    .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
                    .optional(),
                role: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                status: z.enum([
                    "active",
                    "suspended",
                    "invited",
                    "deactivated",
                    "pending_verification",
                    "disabled",
                ]),
                profile: z
                    .object({
                    department: z.string(),
                    title: z.string(),
                    timezone: z.string(),
                    locale: z.string(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                authentication: z
                    .object({
                    lastLoginAt: z.union([z.string(), z.null()]),
                    mfaEnrolled: z.boolean(),
                    mfaMethod: z.union([z.string(), z.null()]),
                    passwordChangedAt: z.union([z.string(), z.null()]),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                tags: z.array(z.string()).optional(),
                createdBy: z.union([z.string(), z.null()]).optional(),
                updatedBy: z.union([z.string(), z.null()]).optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "patch",
        path: "/orgs/:orgId/identity/users/:userId",
        alias: "PatchOrgIdentityUser",
        description: `Updates user profile and identity fields. Request body (UpdateUserRequest) aligns with patchable fields in IDENTITY_USERS in .samples/identity.samples.ts (displayName, firstName, lastName, avatar, phone, roles, role, status, profile, metadata, tags). Server-managed fields such as authentication, createdBy, updatedBy are not accepted.`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: PatchOrgIdentityUser_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "userId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                entityType: z.string().optional(),
                id: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                userId: z
                    .string()
                    .regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/)
                    .optional(),
                orgId: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
                email: z.string().email(),
                emailVerified: z.boolean().optional(),
                displayName: z.string(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                avatar: z.union([z.string(), z.null()]).optional(),
                avatarS3Key: z.union([z.string(), z.null()]).optional(),
                coverS3Key: z.union([z.string(), z.null()]).optional(),
                phone: z.union([z.string(), z.null()]).optional(),
                phoneVerified: z.boolean().optional().default(false),
                roles: z
                    .array(z.enum(["platform_admin", "org_admin", "user", "viewer"]))
                    .optional(),
                role: z.string().optional(),
                permissions: z.array(z.string()).optional(),
                status: z.enum([
                    "active",
                    "suspended",
                    "invited",
                    "deactivated",
                    "pending_verification",
                    "disabled",
                ]),
                profile: z
                    .object({
                    department: z.string(),
                    title: z.string(),
                    timezone: z.string(),
                    locale: z.string(),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                authentication: z
                    .object({
                    lastLoginAt: z.union([z.string(), z.null()]),
                    mfaEnrolled: z.boolean(),
                    mfaMethod: z.union([z.string(), z.null()]),
                    passwordChangedAt: z.union([z.string(), z.null()]),
                })
                    .partial()
                    .passthrough()
                    .optional(),
                metadata: z.object({}).partial().passthrough().optional(),
                tags: z.array(z.string()).optional(),
                createdBy: z.union([z.string(), z.null()]).optional(),
                updatedBy: z.union([z.string(), z.null()]).optional(),
                createdAt: z.string().datetime({ offset: true }).optional(),
                updatedAt: z.string().datetime({ offset: true }).optional(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "delete",
        path: "/orgs/:orgId/identity/users/:userId",
        alias: "DeleteOrgIdentityUser",
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "userId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.void(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "get",
        path: "/orgs/:orgId/identity/users/:userId/onboarding",
        alias: "GetOrgIdentityUserOnboarding",
        description: `Retrieves the onboarding status for a specific user. Returns which steps
have been completed and which are pending for the user.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "userId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({ accessToken: z.string(), refreshToken: z.string() })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/users/:userId/onboarding",
        alias: "CompleteOrgIdentityUserOnboardingStep",
        description: `Marks a specific onboarding step as completed for the user. Updates user
metadata to track onboarding progress.
`,
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: CompleteOrgIdentityUserOnboardingStep_Body,
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "userId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z
            .object({
            data: z
                .object({
                success: z.boolean(),
                stepId: z.string(),
                completed: z.boolean(),
            })
                .passthrough(),
            meta: z
                .object({
                correlationId: z
                    .string()
                    .regex(/^[a-z]{3}_[0-9a-hjkmnp-tv-z]{26}$/),
                timestamp: z.string().datetime({ offset: true }),
                requestId: z.string().optional(),
            })
                .passthrough(),
        })
            .passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
    {
        method: "post",
        path: "/orgs/:orgId/identity/users/:userId/password-reset",
        alias: "RequestOrgIdentityUserPasswordReset",
        requestFormat: "json",
        parameters: [
            {
                name: "body",
                type: "Body",
                schema: z.object({}).partial().passthrough(),
            },
            {
                name: "orgId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
            {
                name: "userId",
                type: "Path",
                schema: z.string().regex(/^idt_[0-9a-hjkmnp-tv-z]{26}$/),
            },
        ],
        response: z.object({ message: z.string() }).partial().passthrough(),
        errors: [
            {
                status: 400,
                description: `Invalid request payload or query parameters (400)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 401,
                description: `Missing or invalid authentication credentials (401)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 403,
                description: `Insufficient permissions (403)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
            {
                status: 404,
                description: `Resource not found (404)`,
                schema: z
                    .object({
                    type: z.string().url(),
                    title: z.string(),
                    status: z.number().int().gte(100).lte(599),
                    detail: z.string().optional(),
                    instance: z.string().optional(),
                    code: z.string().optional(),
                    traceId: z.string().optional(),
                    errors: z
                        .array(z
                        .object({
                        field: z.string(),
                        message: z.string(),
                        code: z.string().optional(),
                    })
                        .passthrough())
                        .optional(),
                })
                    .passthrough(),
            },
        ],
    },
]);
export const api = new Zodios("https://api.virialai.com/v1", endpoints);
export function createApiClient(baseUrl, options) {
    return new Zodios(baseUrl, endpoints, options);
}
//# sourceMappingURL=identity.zod.schema.js.map